/**
 * What a global money figure leaves out, one line per vertical that is not
 * `ok` (api#1121). The figure above it is the API's per-currency total of the
 * verticals that could be counted; this list is what makes that total honest:
 *
 *   partial         "Trucking: 3 unpriced, not in the total"
 *   not_computable  "Surface Rentals: — A rental on rent has no day rate."
 *   failed          "Downhole: couldn't load. Not in the total."
 *
 * An `ok` vertical draws nothing. A status the screen does not know claims no
 * meaning: it reads as not counted, never as fine.
 */
import type { MoneyStatus, MoneyVertical } from '../../types/overview-money';

/** Exhaustive over the routes' verticals. An unknown key is named as such, never guessed. */
const VERTICAL_LABEL: Record<MoneyVertical, string> = {
  casing_running: 'Casing',
  trucking: 'Trucking',
  surface_rentals: 'Surface Rentals',
  downhole_tools: 'Downhole',
  pressure_testing: 'Pressure Testing',
  ndt_inspection: 'NDT',
  pipeline_facility: 'Pipeline',
  well_testing: 'Well Testing',
  well_servicing: 'Well Servicing',
};
export const moneyVerticalLabel = (v: string) => VERTICAL_LABEL[v as MoneyVertical] ?? 'Another vertical';

export interface MoneyVerticalStatus {
  vertical: string;
  status: MoneyStatus;
  reason: string | null;
  /** Revenue only; receivables carry none. */
  unpriced_count?: number;
}

export function verticalNote(v: MoneyVerticalStatus): string | null {
  const name = moneyVerticalLabel(v.vertical);
  switch (v.status) {
    case 'ok':
      return null;
    case 'partial': {
      const n = v.unpriced_count ?? 0;
      return n > 0 ? `${name}: ${n} unpriced, not in the total` : `${name}: partly priced, not all in the total`;
    }
    case 'not_computable':
      return `${name}: —${v.reason ? ` ${v.reason}` : ''}`;
    case 'failed':
      return `${name}: couldn’t load. Not in the total.`;
    default:
      return `${name}: not counted.`;
  }
}

export function MoneyVerticalNotes({ verticals, testId }: { verticals: MoneyVerticalStatus[]; testId: string }) {
  const notes = verticals.map(verticalNote).filter((n): n is string => n !== null);
  if (notes.length === 0) return null;
  return (
    <ul data-testid={testId} className="mt-3 space-y-1 border-t border-fieldiq-border pt-3 text-xs text-fieldiq-muted">
      {notes.map((n) => (
        <li key={n}>{n}</li>
      ))}
    </ul>
  );
}
